'use client';
import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useLanguage } from '@/lib/LanguageContext';
import { getTranslations } from '@/lib/i18n';

// metadata removed - client component

const capabilities = [
  {
    icon: 'fas fa-network-wired',
    titleEs: 'Diseño de Redes LAN/WAN',
    titleFr: 'Conception Réseaux LAN/WAN',
    descriptionEs: 'Diseñamos arquitecturas de red robustas y escalables adaptadas a las necesidades de tu organización, garantizando rendimiento, seguridad y disponibilidad.',
    descriptionFr: 'Nous concevons des architectures réseau robustes et évolutives adaptées aux besoins de votre organisation, garantissant performance, sécurité et disponibilité.',
  },
  {
    icon: 'fas fa-wifi',
    titleEs: 'Redes Inalámbricas Corporativas',
    titleFr: 'Réseaux Sans Fil d\'Entreprise',
    descriptionEs: 'Implementación de soluciones Wi-Fi empresariales de alta densidad con cobertura total, segmentación de red y gestión centralizada.',
    descriptionFr: 'Déploiement de solutions Wi-Fi d\'entreprise haute densité avec couverture totale, segmentation réseau et gestion centralisée.',
  },
  {
    icon: 'fab fa-aws',
    titleEs: 'Infraestructura Cloud e Híbrida',
    titleFr: 'Infrastructure Cloud et Hybride',
    descriptionEs: 'Migración y gestión de infraestructura en la nube con soluciones híbridas que combinan lo mejor del entorno local y los servicios cloud.',
    descriptionFr: 'Migration et gestion d\'infrastructure cloud avec des solutions hybrides combinant le meilleur de l\'environnement local et des services cloud.',
  },
  {
    icon: 'fas fa-server',
    titleEs: 'Servidores y Virtualización',
    titleFr: 'Serveurs et Virtualisation',
    descriptionEs: 'Instalación, configuración y administración de servidores físicos y virtuales, optimizando recursos y reduciendo costos operativos.',
    descriptionFr: 'Installation, configuration et administration de serveurs physiques et virtuels, optimisant les ressources et réduisant les coûts opérationnels.',
  },
  {
    icon: 'fas fa-shield-alt',
    titleEs: 'Seguridad Perimetral',
    titleFr: 'Sécurité Périmétrique',
    descriptionEs: 'Implementación de firewalls, VPNs, sistemas de detección de intrusos y políticas de seguridad para proteger tu infraestructura crítica.',
    descriptionFr: 'Déploiement de pare-feux, VPN, systèmes de détection d\'intrusion et politiques de sécurité pour protéger votre infrastructure critique.',
  },
  {
    icon: 'fas fa-sync-alt',
    titleEs: 'Monitoreo y Continuidad',
    titleFr: 'Surveillance et Continuité',
    descriptionEs: 'Supervisión proactiva de la infraestructura con alertas en tiempo real, planes de contingencia y estrategias de recuperación ante desastres.',
    descriptionFr: 'Supervision proactive de l\'infrastructure avec alertes en temps réel, plans de contingence et stratégies de reprise après sinistre.',
  },
];

const sectors = [
  { icon: 'fas fa-hospital', labelEs: 'Salud', labelFr: 'Santé' },
  { icon: 'fas fa-university', labelEs: 'Educación', labelFr: 'Éducation' },
  { icon: 'fas fa-building', labelEs: 'Gobierno', labelFr: 'Gouvernement' },
  { icon: 'fas fa-industry', labelEs: 'Industria', labelFr: 'Industrie' },
  { icon: 'fas fa-landmark', labelEs: 'Finanzas', labelFr: 'Finance' },
  { icon: 'fas fa-broadcast-tower', labelEs: 'Telecomunicaciones', labelFr: 'Télécommunications' },
];

export default function InfraestructuraRedesPage() {
  const { language } = useLanguage();
  const t = getTranslations(language);
  const s = t?.services;
  const isFr = language === 'fr';

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Nunito Sans', 'Raleway', arial, sans-serif" }}>
      <Navbar />
      <section className="relative overflow-hidden" style={{ paddingTop: '160px', paddingBottom: '80px', backgroundColor: '#0a0a0a' }}>
        <div className="absolute top-20 right-10 opacity-20 hidden lg:block"><img src="https://innobox.cl/wp-content/uploads/2021/07/Dots-4.svg" alt="" className="w-40 h-40" /></div>
        <div className="absolute bottom-0 left-0 opacity-10 hidden lg:block"><img src="https://innobox.cl/wp-content/uploads/2021/07/Abstract-Circle.svg" alt="" className="w-64 h-64" /></div>
        <div className="max-w-[1200px] mx-auto px-[35px] relative z-10">
          <nav className="mb-8 flex items-center gap-2 text-sm" style={{ color: '#888' }}>
            <Link href="/" className="hover:text-[#1AEF0F] transition-colors">{s?.breadcrumbHome}</Link>
            <span>/</span>
            <Link href="/#servicios" className="hover:text-[#1AEF0F] transition-colors">{s?.breadcrumbServices}</Link>
            <span>/</span>
            <span style={{ color: '#1AEF0F' }}>{isFr ? 'Infrastructure et Réseaux' : 'Infraestructura y Redes'}</span>
          </nav>
          <div className="flex flex-col lg:flex-row items-start gap-12">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 mb-6 px-4 py-2" style={{ border: '1px solid #1AEF0F', color: '#1AEF0F', fontSize: '13px', letterSpacing: '2px' }}>
                <i className="fab fa-aws" /><span>{isFr ? 'INFRASTRUCTURE IT' : 'INFRAESTRUCTURA TI'}</span>
              </div>
              <h1 className="text-white font-bold mb-6" style={{ fontSize: 'clamp(32px, 4vw, 56px)', fontFamily: "'Titillium Web', sans-serif", lineHeight: '1.15' }}>
                {isFr ? 'Infrastructure' : 'Infraestructura'}<br /><span style={{ color: '#1AEF0F' }}>{isFr ? 'et Réseaux de Données' : 'y Redes de Datos'}</span>
              </h1>
              <p className="mb-8" style={{ fontSize: '18px', color: '#aaa', lineHeight: '1.7', maxWidth: '560px' }}>
                {isFr ? 'Connectivité sécurisée, évolutive et haute performance pour les organisations qui ne peuvent pas se permettre d\'interruptions. Nous concevons et gérons l\'épine dorsale technologique de votre entreprise.' : 'Conectividad segura, escalable y de alto rendimiento para organizaciones que no pueden permitirse interrupciones. Diseñamos y gestionamos la columna vertebral tecnológica de tu empresa.'}
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="https://calendar.app.google/qrMMuz1WHJJ4eoab8" target="_blank" rel="noopener noreferrer" className="group relative inline-flex items-center overflow-hidden" style={{ border: '1px solid #1AEF0F', padding: '14px 32px', fontSize: '15px', fontWeight: 'bold', color: '#1AEF0F', backgroundColor: 'transparent', transition: 'all 300ms ease-out' }}>
                  <span className="relative z-10 transition-colors duration-300 group-hover:text-black">{isFr ? 'Évaluer mon Infrastructure' : 'Evaluar mi Infraestructura'}</span>
                  <span className="absolute inset-0 transform translate-x-full group-hover:translate-x-0 transition-transform duration-300" style={{ backgroundColor: '#1AEF0F', zIndex: 0 }} />
                </a>
                <Link href="/#servicios" className="inline-flex items-center gap-2 text-white hover:text-[#1AEF0F] transition-colors" style={{ fontSize: '15px', padding: '14px 0' }}>
                  <i className="fas fa-arrow-left text-sm" />{isFr ? 'Voir tous les services' : 'Ver todos los servicios'}
                </Link>
              </div>
            </div>
            <div className="lg:w-72 grid grid-cols-2 gap-4">
              {[
                { value: '99.9%', labelEs: 'Uptime garantizado', labelFr: 'Uptime garanti' },
                { value: '+200', labelEs: 'Redes implementadas', labelFr: 'Réseaux déployés' },
                { value: '10G', labelEs: 'Capacidad máx. de red', labelFr: 'Capacité max. réseau' },
                { value: '24/7', labelEs: 'Monitoreo activo', labelFr: 'Surveillance active' },
              ]?.map((stat, i) => (
                <div key={i} className="p-5" style={{ border: '1px solid #222', backgroundColor: '#111' }}>
                  <div className="font-bold mb-1" style={{ fontSize: '28px', color: '#1AEF0F', fontFamily: "'Titillium Web', sans-serif" }}>{stat?.value}</div>
                  <div style={{ fontSize: '12px', color: '#888', lineHeight: '1.4' }}>{isFr ? stat?.labelFr : stat?.labelEs}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      <section style={{ paddingTop: '80px', paddingBottom: '80px', backgroundColor: '#fff' }}>
        <div className="max-w-[1200px] mx-auto px-[35px]">
          <div className="mb-12">
            <h2 className="font-bold text-black mb-3" style={{ fontSize: 'clamp(24px, 2.5vw, 36px)', fontFamily: "'Titillium Web', sans-serif" }}>{s?.capabilitiesTitle}</h2>
            <p style={{ fontSize: '16px', color: '#505050', maxWidth: '600px', lineHeight: '1.6' }}>{isFr ? 'Solutions d\'infrastructure complètes qui assurent la connectivité, la disponibilité et la sécurité de tous les systèmes de votre organisation.' : 'Soluciones de infraestructura completas que aseguran la conectividad, disponibilidad y seguridad de todos los sistemas de tu organización.'}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0">
            {capabilities?.map((cap, i) => (
              <div key={i} className="group p-8 transition-all duration-300 hover:bg-[#f9f9f9]" style={{ borderBottom: '1px solid #E2E2E2', borderRight: i % 3 !== 2 ? '1px solid #E2E2E2' : 'none' }}>
                <div className="mb-5 flex items-center justify-center" style={{ width: '52px', height: '52px', border: '1px solid #E2E2E2', borderRadius: '50%' }}>
                  <i className={`${cap?.icon} text-xl text-black group-hover:text-[#1AEF0F] transition-colors duration-300`} />
                </div>
                <h3 className="font-semibold text-black mb-3" style={{ fontSize: '17px', fontFamily: "'Titillium Web', sans-serif" }}>{isFr ? cap?.titleFr : cap?.titleEs}</h3>
                <p style={{ fontSize: '14px', color: '#505050', lineHeight: '1.6' }}>{isFr ? cap?.descriptionFr : cap?.descriptionEs}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section style={{ paddingTop: '60px', paddingBottom: '80px', backgroundColor: '#f5f5f5' }}>
        <div className="max-w-[1200px] mx-auto px-[35px]">
          <h2 className="font-bold text-black mb-10" style={{ fontSize: 'clamp(22px, 2vw, 30px)', fontFamily: "'Titillium Web', sans-serif" }}>{s?.sectorsTitle}</h2>
          <div className="flex flex-wrap gap-4">
            {sectors?.map((sec, i) => (
              <div key={i} className="flex items-center gap-3 px-6 py-4 bg-white" style={{ border: '1px solid #E2E2E2' }}>
                <i className={`${sec?.icon} text-[#1AEF0F]`} />
                <span style={{ fontSize: '15px', fontWeight: '600', color: '#111' }}>{isFr ? sec?.labelFr : sec?.labelEs}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section style={{ paddingTop: '70px', paddingBottom: '70px', backgroundColor: '#1AEF0F' }}>
        <div className="max-w-[1200px] mx-auto px-[35px] flex flex-col sm:flex-row items-center justify-between gap-6">
          <p className="text-black font-bold m-0" style={{ fontSize: 'clamp(18px, 2vw, 26px)', fontFamily: "'Titillium Web', sans-serif", lineHeight: '1.3' }}>
            {isFr ? 'Votre infrastructure est-elle prête pour l\'avenir ?' : '¿Tu infraestructura está lista para el futuro?'}
          </p>
          <Link href="/contact" className="group relative inline-flex items-center overflow-hidden border border-black text-black bg-transparent flex-shrink-0" style={{ padding: '14px 32px', fontSize: '15px', fontWeight: 'bold', transition: 'all 300ms ease-out' }}>
            <span className="relative z-10 transition-colors duration-300 group-hover:text-white">{s?.ctaBtnContact}</span>
            <span className="absolute inset-0 bg-black transform translate-x-full group-hover:translate-x-0 transition-transform duration-300" style={{ zIndex: 0 }} />
          </Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
